import { useMemo } from 'react';

import { supabaseClient, useUser } from '@auth-v2';
import { useQuery } from '@tanstack/react-query';

import {
    CategoryType,
    fetchAllUiNodes,
    fetchCategories,
    fetchRelations,
    fetchUiGraph,
    fetchUiGraphLines,
    fetchUiNodeType,
    fetchUiParameters,
    UiNodeType,
} from '@data';

type OtherUiNodeType = Exclude<UiNodeType, 'parameter' | 'graph' | 'graph_line'>;

export const getUiNodesKey = (type?: UiNodeType) => ['uiNodes', type ?? 'all'] as const;

export const useUiNodes = (type?: OtherUiNodeType) =>
    useQuery({
        queryKey: getUiNodesKey(type),
        queryFn: () => (type ? fetchUiNodeType(supabaseClient, type) : fetchAllUiNodes(supabaseClient)),
    });

export const useUiParameters = () => {
    const { data: user } = useUser();

    return useQuery({
        queryKey: getUiNodesKey('parameter'),
        queryFn: () => fetchUiParameters(supabaseClient),
        enabled: !!user?.id,
    });
};

export const useUiGraph = () =>
    useQuery({
        queryKey: getUiNodesKey('graph'),
        queryFn: () => fetchUiGraph(supabaseClient),
    });

export const useUiGraphLine = () => {
    const { data: user } = useUser();

    return useQuery({
        queryKey: [...getUiNodesKey('graph_line')],
        queryFn: () => fetchUiGraphLines(supabaseClient),
        enabled: !!user?.id,
    });
};

export const getCategoriesKey = (type?: CategoryType) => ['categories', type ?? 'all'] as const;
export const useCategories = (type?: CategoryType) => {
    return useQuery({
        queryKey: getCategoriesKey(type),
        queryFn: () => fetchCategories(supabaseClient, type),
    });
};

export const getRelationsKey = () => ['relations'] as const;
export const useRelations = () => {
    return useQuery({
        queryKey: getRelationsKey(),
        queryFn: () => fetchRelations(supabaseClient),
    });
};

export const SWING_FOUNDATIONS_KIOSK = 'swing_foundations.default_kiosk';

/**
 * @returns Swing foundation nodes of key positions.
 * Example: P1 - Setup, P2 - Takeaway, etc.
 */
export const useParameterGroups = () => {
    const { data: swingFoundationGroups = [], ...rest } = useUiNodes('swing_foundations');
    const parameterGroups = useMemo(
        () => swingFoundationGroups.filter((group) => group.id !== SWING_FOUNDATIONS_KIOSK),
        [swingFoundationGroups],
    );
    return { ...rest, data: parameterGroups };
};
